<script setup lang="ts">
import './assets/style.scss'
import { useRoute } from 'vue-router'
import { computed, ref, watch } from 'vue'
import { useModel } from './../model/model.ts'
import type { InventoryFieldFilter } from './../model/types.ts'
import { VPreloader, VIcons } from '@/shared/ui'

const route = useRoute()
const objectId = computed(() => Number(route.params.object_id))

const {
  filters,
  selectedIds,
  fieldFilters: selectedFieldFilters,
  isFetching,
  isError,
  toggle,
  toggleOption,
  getCheckedState,
  isIndeterminate,
  selectAll,
  deselectAll,
} = useModel(objectId)

const filter = defineModel<number[]>({ required: true })
const fieldFilters = defineModel<InventoryFieldFilter[]>('fieldFilters', { required: true })

/** Состояние раскрытых фильтров по полям. */
const expanded = ref<Set<number>>(new Set())

const toggleExpand = (nodeId: number) => {
  const s = new Set(expanded.value)
  if (s.has(nodeId)) s.delete(nodeId)
  else s.add(nodeId)
  expanded.value = s
}

const isExpanded = (nodeId: number) => expanded.value.has(nodeId)

/** Синхронизируем выбранные типы и значения полей с фильтром карты. */
watch(
  [selectedIds, selectedFieldFilters],
  ([ids, fields]) => {
    filter.value = ids
    fieldFilters.value = fields
  },
  { immediate: true },
)
</script>

<template>
  <VPreloader v-if="isFetching" :wrapper-height="50" />

  <div v-else-if="isError" class="text-error pa-2">
    {{ $t('itf.filter_load_error') }}
  </div>

  <template v-else>
    <div class="w-inventory-map-filter__actions">
      <VBtn density="compact" size="small" variant="tonal" @click="selectAll">
        {{ $t('itf.select_all') }}
      </VBtn>

      <VBtn density="compact" size="small" variant="tonal" @click="deselectAll">
        {{ $t('itf.deselect_all') }}
      </VBtn>
    </div>

    <div class="w-inventory-map-filter__list">
      <div v-for="item in filters" :key="item.object_type.id" class="w-inventory-map-filter__item">
        <div class="w-inventory-map-filter__header">
          <VCheckbox
            hide-details
            :model-value="getCheckedState(item)"
            :indeterminate="isIndeterminate(item)"
            @click.stop="toggle(item.object_type.id)"
          />

          <VIcons :type="item.object_type.icon" />
          <span
            class="w-inventory-map-filter__name"
            :class="{
              'w-inventory-map-filter__name--expandable': item.options.length,
            }"
            @click.stop="item.options.length && toggleExpand(item.object_type.id)"
          >
            {{ item.object_type.name }}
          </span>

          <VIcons
            v-if="item.options.length"
            type="arrow_drop_down"
            class="w-inventory-map-filter__arrow"
            :class="{
              'w-inventory-map-filter__arrow--open': isExpanded(item.object_type.id),
            }"
            @click.stop="toggleExpand(item.object_type.id)"
          />
        </div>

        <div
          v-if="item.options.length"
          v-show="isExpanded(item.object_type.id)"
          class="w-inventory-map-filter__options"
        >
          <div
            v-for="option in item.options"
            :key="option.id"
            class="w-inventory-map-filter__option"
            @click="toggleOption(item.object_type.id, option.id)"
          >
            <VCheckbox
              hide-details
              :model-value="item.selectedOptionIds.includes(option.id)"
              @click.stop="toggleOption(item.object_type.id, option.id)"
            />

            <span class="w-inventory-map-filter__name">{{ option.name }}</span>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>
